import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { usePage } from '@inertiajs/react'

// Client-driven locale + units formatting, ported from the native app's rules.
//
// - Times, dates, and decimals format with the *resolved UI locale* (the shared
//   `locale` prop from Accept-Language — the same source that drives
//   translations), so times localize too (Korean 오전 9:39, German 09:39, …).
// - BUT the SSR Node runtime and the browser can ship different ICU data — e.g.
//   Node formats Korean time as "AM 11:16" while Chrome gives "오전 11:16". So we
//   render the US default on SSR + the first client render, then switch to the
//   resolved locale on mount. That avoids a hydration mismatch, at the cost of a
//   brief format flash for non-US locales (dates via _week don't skew, so they're
//   left SSR-rendered in-language).
// - Height (ft/m) + distance (mi/km) default from the device region (US/GB→feet,
//   US→miles, else metric) and are user-overridable + persisted (settings page),
//   corrected on the same mount pass.

const CM_TO_FT = 0.0328084
const KM_TO_MI = 0.621371

export function makeFormat({ locale, heightUnit, distanceUnit, tempUnit }) {
  const num = (value, digits) => {
    // Snap values that round to zero to +0. A tiny negative (e.g. a below-datum
    // tide height of -0.04) rounds to a *negative* zero, and Node's ICU (Inertia
    // SSR) and the browser can disagree on whether to show its sign ("0.0" vs
    // "-0.0") — a React hydration mismatch. Real ±values are left untouched.
    let v = Number(value)
    if (Math.round(v * 10 ** digits) === 0) v = 0
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        useGrouping: false,
      }).format(v)
    } catch {
      return v.toFixed(digits)
    }
  }
  const dtf = (opts) => {
    try {
      return new Intl.DateTimeFormat(locale, opts)
    } catch {
      return new Intl.DateTimeFormat('en-US', opts)
    }
  }

  // Height/range from cm (prefers a precomputed *_ft when in feet).
  const height = (cm, ftVal) => {
    if (cm == null && ftVal == null) return ''
    if (heightUnit === 'ft') {
      const ft = ftVal != null ? ftVal : cm * CM_TO_FT
      return num(ft, 1) + "'"
    }
    const m = (cm != null ? cm : ftVal / CM_TO_FT) / 100
    return num(m, 2) + ' m'
  }
  // Map station labels: the same number, with the unit SPELLED. The apostrophe
  // is retired here and only here (station pages and charts keep it): 5.1' is
  // the one unit glyph that disappears at 11 px on a basemap, and a stranger
  // cannot tell whether it is a tide height, a depth, a distance or a wave.
  const mapHeight = (cm, ftVal) => {
    if (cm == null && ftVal == null) return ''
    if (heightUnit === 'ft') {
      const ft = ftVal != null ? ftVal : cm * CM_TO_FT
      return num(ft, 1) + ' ft'
    }
    const m = (cm != null ? cm : ftVal / CM_TO_FT) / 100
    return num(m, 2) + ' m'
  }
  // Chart y-axis ticks: 1 dp for both systems.
  const heightAxis = (cm) => {
    if (cm == null) return ''
    return heightUnit === 'ft' ? num(cm * CM_TO_FT, 1) + "'" : num(cm / 100, 1) + ' m'
  }
  // Current speed is always knots. abs() so signed velocities display positive.
  //
  // "kt", not "kts", to match wind() below — a current station shows both, and
  // one unit spelled two ways on one screen reads as a mistake. kt is also the
  // standard abbreviation, invariant in the plural, and what the official
  // marine bulletins we quote verbatim use. The decimal stays: a current of
  // 2.1 vs 2.4 kt is a real difference, where wind rounds to whole knots.
  const speed = (knots) => (knots == null ? '' : num(Math.abs(knots), 1) + ' kt')

  // Weather formatters. All return '' on null — null means "honestly unknown"
  // in the weather feed and must never render as zero/calm; callers show '—'.
  // Air/water temperature arrives in °F; °C is converted per the tempUnit pref.
  const temp = (f) => {
    if (f == null) return ''
    return tempUnit === 'c' ? num(((f - 32) * 5) / 9, 0) + '°' : num(f, 0) + '°'
  }
  // Wind is always knots (marine standard), whole numbers.
  const wind = (kt) => (kt == null ? '' : num(kt, 0) + ' kt')
  // Visibility is always nautical miles (marine standard); one decimal under
  // 2 nm where the difference matters, whole above.
  // Unit labels that vary by locale, not by unit preference. Nautical miles:
  // CJK weather products write the word (13海里 / 13해리, closed up — no space
  // before a CJK unit word); Latin locales keep the marine "nm". Pressure:
  // millibars are a US convention (NWS marine bulletins), everyone else —
  // including the UK — reads hPa (numerically identical).
  const nmLabel = { ja: '海里', ko: '해리', 'zh-Hans': '海里', 'zh-Hant': '海里' }[locale]
  const usEnglish = locale === 'en' || locale === 'en-US'
  const visibility = (nm) =>
    nm == null ? '' : num(nm, nm < 2 ? 1 : 0) + (nmLabel != null ? nmLabel : ' nm')
  const pressure = (mb) => (mb == null ? '' : num(mb, 0) + (usEnglish ? ' mb' : ' hPa'))
  const percent = (p) => (p == null ? '' : num(p, 0) + '%')
  // Precipitation intensity arrives in in/hr; metric-height users get mm/h
  // (the SI-style form — "hr" is an anglophone habit that reads oddly abroad).
  const precipRate = (inhr) => {
    if (inhr == null) return ''
    return heightUnit === 'ft' ? num(inhr, 2) + ' in/hr' : num(inhr * 25.4, 1) + ' mm/h'
  }

  // Distance from meters.
  const distance = (meters) => {
    if (meters == null) return ''
    const km = meters / 1000
    return distanceUnit === 'mi' ? num(km * KM_TO_MI, 1) + ' mi' : num(km, 1) + ' km'
  }

  // An instant (ISO w/ offset for tides, UTC for sun/moon) in the station tz.
  const time = (iso, timeZone) => {
    if (!iso) return ''
    try {
      return dtf({ hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(iso))
    } catch {
      return ''
    }
  }
  const timeUnix = (sec, timeZone) =>
    sec == null ? '' : time(new Date(sec * 1000).toISOString(), timeZone)
  // Hour only ("6 AM"), for dense columns where ":00" on every row is width
  // spent on a digit pair that never varies — the weather feed is on the hour
  // throughout, hourly then 3-hourly.
  //
  // Two guards, because neither assumption holds everywhere. Minutes are read
  // back in the station's own zone, since a whole-hour UTC instant lands on
  // :30 or :45 in India, Nepal and the Chathams — those render the full time.
  // And 24-hour locales keep their minutes too: "14:00" is how they write a
  // time, where a bare "14" reads as a quantity.
  const timeHour = (iso, timeZone) => {
    if (!iso) return ''
    try {
      const d = new Date(iso)
      const f = dtf({ hour: 'numeric', timeZone })
      if (!f.resolvedOptions().hour12) return time(iso, timeZone)
      const minute = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
        timeZone,
      })
        .formatToParts(d)
        .find((p) => p.type === 'minute')?.value
      return minute === '00' ? f.format(d) : time(iso, timeZone)
    } catch {
      return time(iso, timeZone)
    }
  }
  // Short time drops the AM/PM (chart extrema; the axis disambiguates).
  const timeShort = (iso, timeZone) => time(iso, timeZone).replace(/\s*[AP]M$/i, '')
  const timeShortUnix = (sec, timeZone) => timeUnix(sec, timeZone).replace(/\s*[AP]M$/i, '')

  // "Sat, Jul 4" from a YYYY-MM-DD (parsed as local).
  const date = (dateStr) => {
    if (!dateStr) return ''
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr)
    const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(dateStr)
    try {
      return dtf({ weekday: 'short', month: 'short', day: 'numeric' }).format(d)
    } catch {
      return dateStr
    }
  }
  // "Sat, Jul 4, 5:00 AM" — an instant (ISO w/ offset), in the station tz. For
  // windows that can span/cross days (warnings), where a bare time is
  // ambiguous about which day it lands on.
  const dateTime = (iso, timeZone) => {
    if (!iso) return ''
    try {
      return dtf({
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      }).format(new Date(iso))
    } catch {
      return ''
    }
  }

  return {
    height,
    mapHeight,
    heightAxis,
    speed,
    distance,
    time,
    timeHour,
    timeUnix,
    timeShort,
    timeShortUnix,
    date,
    dateTime,
    temp,
    wind,
    visibility,
    pressure,
    percent,
    precipRate,
  }
}

// SSR / first-client-render defaults (US-centric).
const SSR_DEFAULT = { locale: 'en-US', heightUnit: 'ft', distanceUnit: 'mi', tempUnit: 'f' }
const STORAGE_KEY = 'tnm.units'

export function detectRegion() {
  try {
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language]
    for (const l of langs) {
      const m = /-([A-Za-z]{2})\b/.exec(l || '')
      if (m) return m[1].toUpperCase()
    }
  } catch {
    /* SSR / no navigator */
  }
  return 'US' // US-centric fallback when no region is available
}

function readStored() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

// Region defaults (native rules) with a persisted override taking precedence.
// Imperial (feet/miles) is essentially US-only, with the UK using feet for height
// but km for distance. We decide from the resolved UI locale (the user's primary
// language): a NON-English UI is always metric — this is what stops a Korean user
// whose browser also lists en-US from getting feet (detectRegion would otherwise
// skip the region-less `ko` tag and read the region off `en-US`). For an English
// UI we do trust the device region, to tell US / GB / AU / … apart precisely.
export function resolveUnits(locale) {
  const stored = readStored()
  const isEnglish = String(locale || 'en')
    .toLowerCase()
    .startsWith('en')
  const region = isEnglish ? detectRegion() : ''
  const heightUnit = ['ft', 'm'].includes(stored.heightUnit)
    ? stored.heightUnit
    : region === 'US' || region === 'GB'
      ? 'ft'
      : 'm'
  const distanceUnit = ['mi', 'km'].includes(stored.distanceUnit)
    ? stored.distanceUnit
    : region === 'US'
      ? 'mi'
      : 'km'
  // Fahrenheit is US-only (the UK forecasts in °C).
  const tempUnit = ['f', 'c'].includes(stored.tempUnit)
    ? stored.tempUnit
    : region === 'US'
      ? 'f'
      : 'c'
  return { heightUnit, distanceUnit, tempUnit }
}

const UnitsContext = createContext({
  fmt: makeFormat(SSR_DEFAULT),
  prefs: SSR_DEFAULT,
  setUnit: () => {},
})

export function UnitsProvider({ children }) {
  // Resolved UI locale (server-shared). Applied on mount (not the initial render)
  // so SSR + the first client render both use the stable US default — see the
  // header note on Node/browser ICU skew.
  const locale = usePage().props.locale || SSR_DEFAULT.locale
  const [prefs, setPrefs] = useState(SSR_DEFAULT)

  useEffect(() => {
    // Correct locale + unit prefs on mount (locale-based defaults + persisted overrides).
    setPrefs({ locale, ...resolveUnits(locale) })
  }, [locale])

  const setUnit = (key, val) => {
    setPrefs((p) => ({ ...p, [key]: val }))
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readStored(), [key]: val }))
    } catch {
      /* private mode / no storage */
    }
  }

  const fmt = useMemo(() => makeFormat(prefs), [prefs])
  const value = useMemo(() => ({ fmt, prefs, setUnit }), [fmt, prefs])
  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>
}

export const useFormat = () => useContext(UnitsContext).fmt
export function useUnitPrefs() {
  const { prefs, setUnit } = useContext(UnitsContext)
  return { prefs, setUnit }
}
